import { START_LOCATION, createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw } from 'vue-router';
import { can, AppPermissions } from '@/utils/permissions';
import { isStudentRouteAllowed, studentLandingRoute } from '@/utils/studentPortalMode';
import { userHomeRoute } from '@/utils/portalAccess';
import { portalRedirect } from './portalGuard';
import { isTenantHost } from '@/composables/useBranding';
import { useUserStore } from '@/store/userStore';
import { forceCleanupTourDom } from '@/composables/tour/tourCleanup';
import { installChunkReloadRecovery } from './chunkReloadRecovery';

import TeacherView from '../views/TeacherView.vue';
import Home from '../components/TeacherDashboard/Home.vue';
import TimeTable from '../components/TeacherDashboard/TimeTable.vue';
import Students from '../components/TeacherDashboard/Students.vue';
import { studentReportRoute } from './studentReportRoute';
import { reportsRoutes } from './reportsRoutes';
import RecordScore from '../components/TeacherDashboard/RecordScore.vue';
import SchoolCopyResult from '../components/Students/SchoolCopyResult/SchoolCopyResult.vue';
import Assignment from '../components/TeacherDashboard/Assignment.vue';
import MarkBook from '../components/TeacherDashboard/MarkBook/MarkBook.vue';
import AdminDashboardView from '../views/AdmiDashboardView.vue';
import StudentDashboardView from '../views/StudentDashboardView.vue';
import ParentDashboardView from '../views/ParentDashboardView.vue';

import HomeDash from '../components/DashBoard/HomeDash.vue';
import GradeSystem from '@/views/AdminDashboard/GradeSystem.vue';
import Student from '@/views/AdminDashboard/Student.vue';

import Teacher from '@/views/AdminDashboard/Teacher.vue';

import Timetable from '@/views/AdminDashboard/Timetable.vue';

import Results from '@/views/AdminDashboard/Results.vue';
import EClass from '@/views/AdminDashboard/EClass.vue';

import Settings from '@/views/AdminDashboard/Settings.vue';

// Students
import StudentHome from '../components/StudentDashboard/StudentHome/StudentHome.vue';
import StudentTimetable from '../components/StudentDashboard/TimeTable/TimeTable.vue';
import Result from '@/views/StudentDashboard/Result.vue';
import StudentEClass from '@/views/StudentDashboard/EClass.vue';

import Exam from '@/views/StudentDashboard/Exam.vue';
import ExamPage from '@/components/StudentDashboard/Exam/ExamPage.vue';
import TestPage from '@/components/StudentDashboard/Exam/TestPage.vue';
import StudentSettings from '@/views/StudentDashboard/Settings.vue';
import { routeNames } from '@/constants/route-details.constant';
import { LocalStorageKeys } from '@/constants/LocalStorageKey.constant';
import { useOpenModalAsRoute } from '@/store/useOpenModalAsRoute';
import Course from '@/views/AdminDashboard/Tertiary/Course.vue';

import AcceptInvitation from '@/views/AcceptInvitation.vue';
import RejectInvitation from '@/views/RejectInvitation.vue';
import NewLogin from '@/views/AuthDashboard/Login.vue';
import CreateAccount from '@/views/AuthDashboard/CreateAccount.vue';
import LandingPage from '@/views/LandingPage.vue';
import CBTResult from '@/components/DashBoard/CBT/CBTResults/CBTResult.vue';
import ForgotPassword from '@/components/Auth/ForgotPassword.vue';
import ResetPassword from '@/components/Auth/ResetPassword.vue';
import ViewStudentOverview from '@/components/DashBoard/Student/Overview/ViewStudentOverview.vue';
import CourseRegistration from '@/components/StudentDashboard/CourseRegistration/CourseRegistration.vue';
import CourseDetails from '@/components/DashBoard/Tertiary/Courses/Course/CourseDetails.vue';
import TeacherDetails from '@/components/DashBoard/Teacher/TeacherDetails.vue';
import { cbtMonitoringRoutes } from '@/features/cbt-monitoring';
import { loginLocation } from '@/utils/authRedirect';
import { DATA_REQUESTS_LOCATION } from '@/constants/student-fields-location.constant';

const routes: RouteRecordRaw[] = [
  { path: '/accept-invitation/:token', name: 'accept-invitation', component: AcceptInvitation },
  { path: '/reject-invitation/:token', name: 'reject-invitation', component: RejectInvitation },

  { path: '/', name: 'landing', component: LandingPage },
  { path: '/features', name: 'landing-features', component: () => import('@/views/landing/FeaturesView.vue') },
  { path: '/pricing', name: 'landing-pricing', component: () => import('@/views/landing/PricingView.vue') },
  { path: '/for-your-role', name: 'landing-for-your-role', component: () => import('@/views/landing/ForYourRoleView.vue') },

  { path: '/login', name: 'login', component: NewLogin },
  { path: '/create-account', name: 'create-account', component: CreateAccount },
  //{ path: '/teacher-login', name: 'teacher-login', component: TeacherLogin },
  { path: '/forgot-password', name: 'forgot-password', component: ForgotPassword },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: ResetPassword,
    // The page is reachable only through the emailed link. Typing
    // /reset-password by hand (no token/email) bounces to forgot-password
    // rather than showing a reset form that could never succeed.
    beforeEnter: (to) => {
      const token = to.query.token?.toString().trim();
      const email = to.query.email?.toString().trim();
      if (!token || !email) return { name: 'forgot-password' };
      return true;
    },
  },
  // Student onboarding invite / set-password (public): links arrive by email
  // from the admission-conversion flow (sms-be #252).
  {
    path: '/student/set-password',
    name: 'student-set-password',
    component: () => import('@/components/Auth/StudentSetPassword.vue'),
  },
  { path: '/switch-school', name: 'switch-school', component: () => import('@/views/AuthDashboard/SchoolSwitchView.vue'), meta: { requiresAuth: true } },
  { path: '/select-school', name: 'select-school', component: () => import('@/views/AuthDashboard/SelectSchool.vue'), meta: { requiresAuth: true } },

  // Public Forms
  {
    path: '/forms/:slug',
    name: 'public-form-view',
    component: () => import('@/views/Public/Forms/PublicFormView.vue'),
  },
  {
    path: '/public/payment/:billId/checkout',
    name: 'public-payment-checkout',
    component: () => import('@/views/Public/Payment/GuestPaymentCheckout.vue'),
  },
  {
    path: '/public/payment/status',
    name: 'public-payment-status',
    component: () => import('@/views/Public/Payment/GuestPaymentStatus.vue'),
  },
  {
    path: '/application-status',
    name: 'application-status',
    component: () => import('@/views/Public/Forms/ApplicantStatusView.vue'),
  },
  {
    path: '/reference/:token',
    name: 'reference-form',
    component: () => import('@/views/Public/Forms/ReferenceFormView.vue'),
  },
  {
    path: '/transcripts/:slug',
    name: 'public-transcript-request',
    component: () => import('@/views/Public/Transcript/PublicTranscriptRequestView.vue'),
    props: true,
  },
  {
    path: '/transcripts-status',
    name: 'public-transcript-status',
    component: () => import('@/views/Public/Transcript/TranscriptStatusCheckView.vue'),
  },
  {
    path: '/verify-certificate',
    name: 'public-certificate-verification',
    component: () => import('@/views/Public/Certificate/CertificateVerificationView.vue'),
  },

  // Guardian Portal — public auth routes
  {
    path: '/parent/accept-invitation/:token',
    name: 'parent-accept-invitation',
    component: () => import('@/views/ParentDashboard/AcceptInvitation.vue'),
  },
  {
    path: '/parent/register',
    name: 'parent-register',
    component: () => import('@/views/ParentDashboard/ParentRegister.vue'),
  },

  // Admin Dashboard routes
  {
    path: '/admin',
    component: AdminDashboardView,
    redirect: { name: 'admin-home' }, // Redirect to /admin/home
    children: [
      { path: 'home', name: 'admin-home', component: HomeDash, meta: { requiresAuth: true } },
      { path: 'grade', name: 'admin-grade-system', component: GradeSystem, meta: { requiresAuth: true } },
      // Broadsheet templates merged into the Results hub (Broadsheet tab) — redirect kept for old links.
      { path: 'broadsheet-templates', redirect: { path: '/admin/results', query: { tab: 'broadsheet' } } },
      { path: 'broadsheet-reports', name: 'admin-broadsheet-reports', component: () => import('@/components/DashBoard/Broadsheet/BroadsheetReport.vue'), meta: { requiresAuth: true, title: 'Broadsheet Reports' } },
      // Summary templates merged into the Results hub (Summary tab) — redirect kept for old links.
      { path: 'summary-templates', redirect: { path: '/admin/results', query: { tab: 'summary' } } },
      {
        path: 'announcements',
        name: 'admin-announcements',
        component: () => import('@/views/AdminDashboard/Announcements/AnnouncementsView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Announcements',
          permission: {
            action: AppPermissions.SchoolAnnouncements.Actions.View,
            resource: AppPermissions.SchoolAnnouncements.Resource,
          },
        },
      },
      { path: 'student', name: 'admin-student', component: Student, meta: { requiresAuth: true, permission: { action: AppPermissions.Students.Actions.View, resource: AppPermissions.Students.Resource } } },
      studentReportRoute,
      ...reportsRoutes,
      // Data requests live under Settings → Custom Fields → Students; old links land there.
      { path: 'data-requests', name: 'admin-student-data-requests', redirect: DATA_REQUESTS_LOCATION },
      {
        path: 'data-requests/:id',
        name: 'admin-student-data-request',
        component: () => import('@/views/AdminDashboard/StudentDataRequests/StudentDataRequestDetailView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Data request',
          permission: { action: AppPermissions.Students.Actions.View, resource: AppPermissions.Students.Resource },
        },
      },
      {
        path: 'student/:id/overview',
        name: 'StudentOverview',
        component: ViewStudentOverview,
        meta: { requiresAuth: true },
      },
      { path: 'teacher', name: 'admin-teacher', component: Teacher, meta: { requiresAuth: true, permission: { action: AppPermissions.Teachers.Actions.View, resource: AppPermissions.Teachers.Resource } } },
      {
        path: '/teachers/:id',
        name: 'teacher-details',
        component: TeacherDetails,
      },
      {
        path: 'invited-staff',
        name: 'admin-invited-staff',
        component: () => import('@/views/AdminDashboard/InvitedStaff.vue'),
        meta: { requiresAuth: true },
      },

      {
        path: 'subjects',
        name: 'Subjects',
        component: () => import('@/views/subjects/SubjectsView.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: 'subjects/:id',
        name: 'SubjectDetails',
        component: () => import('@/views/subjects/SubjectDetailsView.vue'),
        props: true,
        meta: { requiresAuth: true },
      },
      {
        path: 'subject-offerings',
        name: 'SubjectOfferings',
        component: () => import('@/views/AdminDashboard/SubjectOfferingsView.vue'),
        meta: { requiresAuth: true, title: 'Subject Offerings', permission: { action: AppPermissions.Subjects.Actions.Update, resource: AppPermissions.Subjects.Resource } },
      },
      {
        path: 'guardians',
        name: 'Guardians',
        component: () => import('@/views/AdminDashboard/Guardians/GuardiansView.vue'),
        meta: { requiresAuth: true, title: 'Guardians', permission: { action: AppPermissions.Guardians.Actions.View, resource: AppPermissions.Guardians.Resource } },
      },
      {
        path: 'guardians/:id',
        name: 'GuardianDetails',
        component: () => import('@/views/AdminDashboard/Guardians/GuardianDetailsView.vue'),
        props: true,
        meta: { requiresAuth: true, permission: { action: AppPermissions.Guardians.Actions.View, resource: AppPermissions.Guardians.Resource } },
      },
      {
        path: 'schemes-of-work',
        name: 'SchemesOfWork',
        component: () => import('@/views/AdminDashboard/Curriculum/SchemesOfWorkView.vue'),
        meta: { requiresAuth: true, title: 'Schemes of Work', permission: { action: AppPermissions.SchemeOfWork.Actions.View, resource: AppPermissions.SchemeOfWork.Resource } },
      },
      {
        path: 'schemes-of-work/:id',
        name: 'SchemeOfWorkDetails',
        component: () => import('@/views/AdminDashboard/Curriculum/SchemeOfWorkDetailsView.vue'),
        props: true,
        meta: { requiresAuth: true, permission: { action: AppPermissions.SchemeOfWork.Actions.View, resource: AppPermissions.SchemeOfWork.Resource } },
      },
      {
        path: 'lesson-notes',
        name: 'LessonNotes',
        component: () => import('@/views/AdminDashboard/Curriculum/LessonNotesView.vue'),
        meta: { requiresAuth: true, title: 'Lesson Notes', permission: { action: AppPermissions.LessonNotes.Actions.View, resource: AppPermissions.LessonNotes.Resource } },
      },
      {
        path: 'lesson-notes/new',
        name: 'LessonNoteCreate',
        component: () => import('@/views/AdminDashboard/Curriculum/LessonNoteEditorView.vue'),
        meta: { requiresAuth: true, title: 'New Lesson Note', permission: { action: AppPermissions.LessonNotes.Actions.Create, resource: AppPermissions.LessonNotes.Resource } },
      },
      {
        path: 'lesson-notes/:id/edit',
        name: 'LessonNoteEdit',
        component: () => import('@/views/AdminDashboard/Curriculum/LessonNoteEditorView.vue'),
        props: true,
        meta: { requiresAuth: true, title: 'Edit Lesson Note', permission: { action: AppPermissions.LessonNotes.Actions.Update, resource: AppPermissions.LessonNotes.Resource } },
      },
      {
        path: 'lesson-notes/:id',
        name: 'LessonNoteDetails',
        component: () => import('@/views/AdminDashboard/Curriculum/LessonNoteDetailsView.vue'),
        props: true,
        meta: { requiresAuth: true, permission: { action: AppPermissions.LessonNotes.Actions.View, resource: AppPermissions.LessonNotes.Resource } },
      },
      { path: 'timetable', name: 'admin-timetable', component: Timetable, meta: { requiresAuth: true, permission: { action: AppPermissions.TimeTable.Actions.View, resource: AppPermissions.TimeTable.Resource } } },
      { path: 'results', name: 'admin-results', component: Results, meta: { requiresAuth: true } },
      // { path: 'cbt', name: 'admin-cbt', component: CBT, meta: { requiresAuth: true, permission: { action: AppPermissions.CBT.Actions.View, resource: AppPermissions.CBT.Resource }, title: 'Forms' } },
      {
        path: 'cbt',
        name: 'admin-cbt',
        component: () => import('@/components/DashBoard/CBT/NewCBTLayout/NewCBTDashboard.vue'),
        meta: { requiresAuth: true, permission: { action: AppPermissions.CBT.Actions.View, resource: AppPermissions.CBT.Resource }, title: 'CBT' },
      },
      // The attendance reminder notification links to the section root.
      { path: 'attendance', redirect: { name: 'admin-daily-attendance' } },
      { path: 'attendance/daily', name: 'admin-daily-attendance', component: () => import('@/views/Admin/Attendance/DailyAttendance.vue'), meta: { requiresAuth: true, permission: { action: AppPermissions.Attendance.Actions.View, resource: AppPermissions.Attendance.Resource } } },
      { path: 'attendance/events', name: 'admin-attendance-events', component: () => import('@/views/Admin/Attendance/AttendanceEvents.vue'), meta: { requiresAuth: true, permission: { action: AppPermissions.AttendanceEvents.Actions.View, resource: AppPermissions.AttendanceEvents.Resource } } },
      { path: 'attendance/events/:id/register', name: 'admin-attendance-event-register', component: () => import('@/views/Admin/Attendance/EventRegister.vue'), meta: { requiresAuth: true, permission: { action: AppPermissions.Attendance.Actions.View, resource: AppPermissions.Attendance.Resource } } },

      {
        path: '/admin/cbt/tests/:testId/results',
        name: 'cbt-test-results',
        component: CBTResult,
        meta: { requiresAuth: true },
      },
      {
        path: '/admin/cbt/grading/:sessionId',
        name: routeNames.CBT_GRADING.name,
        component: () => import('@/components/DashBoard/CBT/Grading/GradingInterface.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: '/admin/cbt/tests/:testId/manage',
        name: 'manage-cbt-test',
        component: () => import('@/components/DashBoard/CBT/NewCBTLayout/Tests/ManageCBTTest.vue'),
        meta: { requiresAuth: true, title: 'Manage Test' },
      },
      {
        path: '/admin/cbt/tests/create',
        name: 'admin-cbt-create',
        component: () => import('@/components/DashBoard/CBT/NewCBTLayout/Tests/ManageCBTTest.vue'),
        meta: { requiresAuth: true, title: 'Create Test' },
      },

      // CBT live monitoring (premium feature) — entire feature lives in src/features/cbt-monitoring/
      ...cbtMonitoringRoutes,

      { path: 'e-class', name: 'admin-eclass', component: EClass, meta: { requiresAuth: true } },
      { path: 'parent-dashboard', name: 'admin-parent-dashboard', component: () => import('@/views/AdminDashboard/ParentDashboard.vue'), meta: { requiresAuth: true, requiresParent: true, title: 'Child Schools', permission: { action: AppPermissions.ChildSchools.Actions.View, resource: AppPermissions.ChildSchools.Resource } } },
      { path: 'chat', name: 'admin-chat', component: () => import('@/views/AdminDashboard/ChatView.vue'), meta: { requiresAuth: true, title: 'Chat' } },
      // AI Assistant (per-school flag gated in-view; server enforces the matrix in D4)
      {
        path: 'ai-assistant',
        name: 'admin-ai-assistant',
        component: () => import('@/views/AiAssistant/AiAssistantChatView.vue'),
        meta: { requiresAuth: true, title: 'AI Assistant', permission: { action: AppPermissions.AiAssistant.Actions.View, resource: AppPermissions.AiAssistant.Resource } },
      },
      { path: 'settings', name: 'admin-settings', component: Settings, meta: { requiresAuth: true, title: 'Settings', permission: { action: AppPermissions.SchoolProfile.Actions.View, resource: AppPermissions.SchoolProfile.Resource } } },
      // Notification centre — owner-scoped, so auth is the only gate (#224)
      { path: 'notifications', name: 'admin-notifications', component: () => import('@/views/NotificationCentre.vue'), meta: { requiresAuth: true, title: 'Notifications' } },

      // My Account (no permission gate)
      { path: 'my-account', name: 'admin-my-account', component: () => import('@/views/AdminDashboard/MyAccount.vue'), meta: { requiresAuth: true, title: 'My Account' } },

      // My Courses (tertiary, no permission gate)
      { path: 'my-courses', name: 'admin-my-courses', component: () => import('@/views/AdminDashboard/Tertiary/MyCourses.vue'), meta: { requiresAuth: true, title: 'My Courses' } },
      { path: 'my-courses/:courseId/students', name: 'admin-course-students', component: () => import('@/views/AdminDashboard/Tertiary/CourseStudents.vue'), meta: { requiresAuth: true, title: 'Course Students' } },
      { path: 'courses/:courseId/student-allocation', name: 'admin-student-allocation', component: () => import('@/views/AdminDashboard/Tertiary/StudentLecturerAllocation.vue'), meta: { requiresAuth: true, title: 'Student-Lecturer Allocation', permission: { action: AppPermissions.LecturerCourseAllocation.Actions.AllocateStudents, resource: AppPermissions.LecturerCourseAllocation.Resource } } },

      // My Subjects (secondary/primary, no permission gate)
      { path: 'my-subjects', name: 'admin-my-subjects', component: () => import('@/views/AdminDashboard/MySubjects.vue'), meta: { requiresAuth: true, title: 'My Subjects' } },
      { path: 'my-subjects/:subjectId/:sectionId', name: 'admin-subject-students', component: () => import('@/views/AdminDashboard/SubjectStudents.vue'), meta: { requiresAuth: true, title: 'Subject Students' } },
      { path: 'my-subjects/:subjectId/assessments', name: 'admin-subject-assessments', component: () => import('@/views/AdminDashboard/SubjectAssessments.vue'), meta: { requiresAuth: true, title: 'Subject Assessments' } },
      { path: 'my-subjects/:subjectId/cbt/create', name: 'admin-subject-cbt-create', component: () => import('@/views/AdminDashboard/SubjectCBTCreate.vue'), meta: { requiresAuth: true, title: 'Create CBT' } },
      { path: 'my-subjects/:subjectId/:sectionId/scores', name: 'admin-subject-scores', component: () => import('@/views/AdminDashboard/SubjectScores.vue'), meta: { requiresAuth: true, title: 'Subject Scores' } },

      { path: 'course', name: 'admin-course', component: Course, meta: { requiresAuth: true } },
      { path: 'registration-approvals', redirect: { path: '/admin/course', query: { tab: 'approvals' } } },
      {
        path: 'course/registration/:studentId',
        name: 'admin-course-registration-view',
        component: () => import('@/components/DashBoard/Tertiary/Courses/ViewCourseRegistration.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: 'course/registration/:studentId/submit',
        name: 'admin-course-registration-submit',
        component: () => import('@/components/DashBoard/Tertiary/Courses/AdminSubmitCourseRegistration.vue'),
        meta: { requiresAuth: true },
      },
      {

        path: '/tertiary/courses/:id',
        name: 'course-details',
        component: CourseDetails,
      },
      {
        path: 'permissions',
        name: 'admin-permissions',
        component: () => import('@/views/AdminDashboard/Permissions/Permissions.vue'),
        meta: { requiresAuth: true, title: 'Permissions', permission: { action: AppPermissions.Permissions.Actions.View, resource: AppPermissions.Permissions.Resource } },
      },

      // Tickets (Admin)
      {
        path: 'tickets',
        name: 'admin-tickets',
        component: () => import('@/views/AdminDashboard/Tickets/SupportDashboard.vue'),
        meta: { requiresAuth: true, title: 'Support' }, // Add specific permission later if needed
      },
      {
        path: 'tickets/settings',
        name: 'admin-tickets-settings',
        component: () => import('@/views/AdminDashboard/Tickets/TicketSettings.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: 'tickets/:id',
        name: 'admin-ticket-detail',
        component: () => import('@/views/AdminDashboard/Tickets/AdminTicketDetail.vue'),
        meta: { requiresAuth: true },
      },

      // Billing
      {
        path: 'billing',
        component: () => import('@/views/AdminDashboard/Billing/BillingLayout.vue'),
        children: [
          {
            path: '',
            redirect: { name: routeNames.ADMIN_BILLING_DASHBOARD.name },
          },
          {
            path: 'dashboard',
            name: routeNames.ADMIN_BILLING_DASHBOARD.name,
            component: () => import('@/views/AdminDashboard/Billing/BillingDashboardView.vue'),
            meta: { requiresAuth: true, title: 'Billing Dashboard' },
          },
          {
            path: 'bills',
            name: routeNames.ADMIN_BILLING_BILLS.name,
            component: () => import('@/views/AdminDashboard/Billing/BillsListView.vue'),
            meta: { requiresAuth: true, title: 'Bills' },
          },
          {
            path: 'bills/:id',
            name: routeNames.ADMIN_BILLING_DETAIL.name,
            component: () => import('@/views/AdminDashboard/Billing/BillDetailView.vue'),
            props: true,
            meta: { requiresAuth: true, title: 'Bill Detail' },
          },
          {
            path: 'bills/:id/students',
            name: routeNames.ADMIN_BILLING_STUDENTS.name,
            component: () => import('@/views/AdminDashboard/Billing/BillStudentsView.vue'),
            props: true,
            meta: { requiresAuth: true, title: 'Bill Students' },
          },
          {
            path: 'transactions',
            name: routeNames.ADMIN_BILLING_TRANSACTIONS.name,
            component: () => import('@/views/AdminDashboard/Billing/TransactionsView.vue'),
            meta: { requiresAuth: true, title: 'Payments' },
          },
          {
            path: 'students',
            name: routeNames.ADMIN_BILLING_STUDENT_DIRECTORY.name,
            component: () => import('@/views/AdminDashboard/Billing/StudentBillingDirectoryView.vue'),
            meta: { requiresAuth: true, title: 'Students' },
          },
          {
            path: 'form-fees',
            name: routeNames.ADMIN_BILLING_FORM_FEES.name,
            component: () => import('@/views/AdminDashboard/Billing/FormFeesView.vue'),
            meta: { requiresAuth: true, title: 'Form Fees' },
          },
          {
            path: 'waiver-codes',
            name: routeNames.ADMIN_BILLING_WAIVER_CODES.name,
            component: () => import('@/views/AdminDashboard/Billing/WaiverCodesView.vue'),
            meta: { requiresAuth: true, title: 'Waiver Codes' },
          },
          {
            path: 'debtors',
            name: routeNames.ADMIN_BILLING_DEBTORS.name,
            component: () => import('@/views/AdminDashboard/Billing/DebtorsView.vue'),
            meta: { requiresAuth: true, title: 'Debtors' },
          },
          {
            path: 'reports',
            name: routeNames.ADMIN_BILLING_REPORTS.name,
            component: () => import('@/views/AdminDashboard/Billing/ReportsView.vue'),
            meta: { requiresAuth: true, title: 'Reports' },
          },
          {
            path: 'items',
            name: routeNames.ADMIN_BILLING_ITEMS.name,
            component: () => import('@/views/AdminDashboard/Billing/BillItemTemplatesView.vue'),
            meta: { requiresAuth: true, title: 'Fee Items' },
          },
          {
            path: 'discounts',
            name: routeNames.ADMIN_BILLING_DISCOUNTS.name,
            component: () => import('@/views/AdminDashboard/Billing/DiscountCategoriesView.vue'),
            meta: { requiresAuth: true, title: 'Discounts' },
          },
          {
            path: 'discounts/:id',
            name: 'admin-billing-discount-detail',
            component: () => import('@/views/AdminDashboard/Billing/DiscountCategoryDetailView.vue'),
            props: true,
            meta: { requiresAuth: true, title: 'Discount Detail' },
          },
          {
            path: 'refunds',
            name: routeNames.ADMIN_BILLING_REFUNDS.name,
            component: () => import('@/views/AdminDashboard/Billing/RefundsExemptionsView.vue'),
            meta: { requiresAuth: true, title: 'Refunds & Exemptions' },
          },
          {
            path: 'students/:id/financials',
            name: routeNames.ADMIN_BILLING_STUDENT_FINANCIALS.name,
            component: () => import('@/views/AdminDashboard/Billing/StudentFinancialProfileView.vue'),
            props: true,
            meta: { requiresAuth: true, title: 'Student Financials' },
          },
        ],
      },

      // Forms
      {
        path: 'forms/admission-letters',
        name: 'admin-admission-letters',
        component: () => import('@/views/AdminDashboard/Forms/AdmissionLettersView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Admission letters',
          permission: { action: AppPermissions.Forms.Actions.View, resource: AppPermissions.Forms.Resource },
        },
      },
      {
        path: 'forms',
        name: 'admin-forms',
        component: () => import('@/views/AdminDashboard/Forms/FormListView.vue'),
        meta: {
          requiresAuth: true,
          permission: { action: AppPermissions.Forms.Actions.View, resource: AppPermissions.Forms.Resource },
        },
      },
      {
        path: 'forms/create',
        name: 'admin-forms-create',
        component: () => import('@/views/AdminDashboard/Forms/FormBuilderView.vue'),
        meta: {
          requiresAuth: true,
          permission: { action: AppPermissions.Forms.Actions.Create, resource: AppPermissions.Forms.Resource },
        },
      },
      {
        path: 'forms/edit/:id',
        name: 'admin-forms-edit',
        component: () => import('@/views/AdminDashboard/Forms/FormBuilderView.vue'),
        meta: {
          requiresAuth: true,
          permission: { action: AppPermissions.Forms.Actions.Update, resource: AppPermissions.Forms.Resource },
        },
      },
      {
        path: 'forms/submissions/:id',
        name: 'admin-forms-submissions',
        component: () => import('@/views/AdminDashboard/Forms/FormSubmissionsView.vue'),
        meta: {
          requiresAuth: true,
          permission: { action: AppPermissions.Forms.Actions.View, resource: AppPermissions.Forms.Resource },
        },
      },
      {
        path: 'forms/submissions/:id/applicant/:submissionId',
        name: 'admin-forms-applicant-review',
        component: () => import('@/views/AdminDashboard/Forms/ApplicantReviewView.vue'),
        meta: {
          requiresAuth: true,
          permission: { action: AppPermissions.Forms.Actions.View, resource: AppPermissions.Forms.Resource },
        },
      },

      // Certificates
      {
        path: 'certificates',
        name: 'admin-certificates',
        component: () => import('@/views/AdminDashboard/Certificates.vue'),
        meta: { requiresAuth: true, title: 'Certificates' },
      },
    ],
  },

  {
    path: '/transactions/receipt/:transactionId',
    name: 'transaction-receipt',
    component: () => import('@/views/transactions/Receipt.vue'),
    props: true,
    meta: { requiresAuth: true },
  },

  // Student account disabled page (outside dashboard layout)
  {
    path: '/student/account-disabled',
    name: 'student-account-disabled',
    component: () => import('@/views/StudentDashboard/AccountDisabled.vue'),
    meta: { requiresAuth: true },
  },

  // Student Dashboard routes
  {
    path: '/student',
    component: StudentDashboardView,
    // CBT-only schools land on the exam surface instead of the dashboard (#326)
    redirect: () => ({ name: studentLandingRoute(useUserStore().isCbtOnlyStudentPortal) }),
    children: [
      { path: 'home', name: 'student-home', component: StudentHome, meta: { requiresAuth: true } },
      // Notification centre — owner-scoped, so auth is the only gate (#224)
      { path: 'notifications', name: 'student-notifications', component: () => import('@/views/NotificationCentre.vue'), meta: { requiresAuth: true, title: 'Notifications' } },
      { path: 'courses', name: 'student-courses', component: CourseRegistration, meta: { requiresAuth: true } },
      { path: 'billing', name: 'student-billing', component: () => import('@/views/StudentDashboard/StudentBilling.vue'), meta: { requiresAuth: true } },
      { path: 'billing/payment-status', name: 'student-payment-status', component: () => import('@/views/StudentDashboard/PaymentStatus.vue'), meta: { requiresAuth: true } },
      { path: 'billing/payments', name: 'student-payment-history', component: () => import('@/views/StudentDashboard/StudentPaymentHistory.vue'), meta: { requiresAuth: true } },
      { path: 'billing/:id', name: 'student-bill-detail', component: () => import('@/views/StudentDashboard/StudentBillDetail.vue'), props: true, meta: { requiresAuth: true } },
      // AI Assistant (renders only when aiAssistantEnabled && aiStudentAccess; server enforces D4)
      {
        path: 'ai-assistant',
        name: 'student-ai-assistant',
        component: () => import('@/views/AiAssistant/AiAssistantChatView.vue'),
        meta: { requiresAuth: true, title: 'AI Assistant' },
      },
      { path: 'eclass', name: 'student-eclass', component: StudentEClass, meta: { requiresAuth: true } },
      { path: 'timetable', name: 'student-timetable', component: StudentTimetable, meta: { requiresAuth: true } },
      { path: 'result', name: 'student-result', component: Result, meta: { requiresAuth: true } },
      {
        path: 'transcripts',
        name: 'student-transcripts',
        component: () => import('@/views/StudentDashboard/MyTranscripts.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: 'notices',
        name: 'student-notice-board',
        component: () => import('@/views/StudentDashboard/NoticeBoard.vue'),
        meta: { requiresAuth: true, title: 'Notice board' },
      },
      { path: 'cbt', name: 'student-cbt', component: Exam, meta: { requiresAuth: true } },
      { path: 'attendance', name: 'student-attendance', component: () => import('@/components/StudentDashboard/Attendance/AttendanceView.vue'), meta: { requiresAuth: true } },

      { path: 'testpage', name: 'test-page', component: TestPage, meta: { requiresAuth: true } },

      // Tickets (Student)
      {
        path: 'forms',
        name: 'student-forms',
        component: () => import('@/views/StudentDashboard/StudentForms.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: 'forms/:id',
        name: 'student-form-fill',
        component: () => import('@/views/StudentDashboard/StudentFormFill.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: 'tickets',
        name: 'student-tickets',
        component: () => import('@/components/StudentDashboard/Tickets/StudentTicketList.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: 'tickets/:id',
        name: 'student-ticket-detail',
        component: () => import('@/components/StudentDashboard/Tickets/StudentTicketDetail.vue'),
        meta: { requiresAuth: true },
      },

      { path: 'settings', name: 'student-settings', component: StudentSettings, meta: { requiresAuth: true } },
      {
        path: ':id/financials',
        name: 'student-financials',
        component: () => import('@/views/StudentFinancialsView.vue'),
        props: true,
        meta: { requiresAuth: true },
      },
      {
        path: ':id/payments',
        name: 'student-payments',
        component: () => import('@/views/StudentPaymentHistoryView.vue'),
        props: true,
        meta: { requiresAuth: true },
      },
      {
        path: 'course-reg',
        name: 'student-course-reg',
        component: () => import('@/components/StudentDashboard/CourseRegistration/CourseRegistration.vue'),
        meta: { requiresAuth: true },
      },
    ],
  },

  { path: '/student/cbtpage/:id', name: 'cbt-page', component: ExamPage, meta: { requiresAuth: true } },

  // Parent/Guardian Portal routes
  {
    path: '/parent',
    component: ParentDashboardView,
    redirect: { name: 'parent-home' },
    // Inherited by every child: portalRedirect keeps non-guardians out.
    meta: { requiresGuardian: true },
    children: [
      { path: 'home', name: 'parent-home', component: () => import('@/views/ParentDashboard/ParentHome.vue'), meta: { requiresAuth: true } },
      { path: 'results', name: 'parent-results', component: () => import('@/views/ParentDashboard/ChildResults.vue'), meta: { requiresAuth: true } },
      { path: 'attendance', name: 'parent-attendance', component: () => import('@/views/ParentDashboard/ChildAttendance.vue'), meta: { requiresAuth: true } },
      { path: 'timetable', name: 'parent-timetable', component: () => import('@/views/ParentDashboard/ChildTimetable.vue'), meta: { requiresAuth: true } },
      { path: 'bills', name: 'parent-bills', component: () => import('@/views/ParentDashboard/ChildBilling.vue'), meta: { requiresAuth: true } },
      { path: 'bills/payment-status', name: 'parent-payment-status', component: () => import('@/views/ParentDashboard/ParentPaymentStatus.vue'), meta: { requiresAuth: true } },
      // The child is part of the URL: the bill belongs to that child, not to whichever one the switcher shows.
      {
        path: 'bills/:studentId/:id',
        name: 'parent-bill-detail',
        component: () => import('@/views/ParentDashboard/ChildBillDetail.vue'),
        props: true,
        meta: { requiresAuth: true },
      },
      { path: 'courses', name: 'parent-courses', component: () => import('@/views/ParentDashboard/ChildCourseRegistration.vue'), meta: { requiresAuth: true } },
      { path: 'transcripts', name: 'parent-transcripts', component: () => import('@/views/ParentDashboard/ChildTranscripts.vue'), meta: { requiresAuth: true } },
      { path: 'announcements', name: 'parent-announcements', component: () => import('@/views/ParentDashboard/ChildAnnouncements.vue'), meta: { requiresAuth: true } },
      // School chat needs the chat permission; guardian-only accounts have none until guardian chat ships.
      {
        path: 'messages',
        name: 'parent-messages',
        component: () => import('@/views/ParentDashboard/ParentMessages.vue'),
        meta: { requiresAuth: true, permission: { action: AppPermissions.Chat.Actions.View, resource: AppPermissions.Chat.Resource } },
      },
      { path: 'tickets', name: 'parent-tickets', component: () => import('@/views/ParentDashboard/ParentTicketList.vue'), meta: { requiresAuth: true } },
      { path: 'tickets/:id', name: 'parent-ticket-detail', component: () => import('@/views/ParentDashboard/ParentTicketDetail.vue'), props: true, meta: { requiresAuth: true } },
      { path: 'profile', name: 'parent-profile', component: () => import('@/views/ParentDashboard/ParentProfile.vue'), meta: { requiresAuth: true } },
      { path: 'settings', name: 'parent-settings', component: () => import('@/views/ParentDashboard/ParentSettings.vue'), meta: { requiresAuth: true } },
    ],
  },

  {
    path: '/bills/:id/transactions',
    name: 'bill-transactions',
    component: () => import('@/views/BillTransactionHistoryView.vue'),
    props: true,
    meta: { requiresAuth: true },
  },

  { path: '/result', name: 'result', component: SchoolCopyResult, props: true },

  // Teacher Dashboard routes
  {
    path: '/teacher',
    component: TeacherView,
    children: [
      { path: 'home', name: 'teacher-home', component: Home },
      { path: 'timetable', name: 'teacher-timetable', component: TimeTable },
      { path: 'students', name: 'teacher-students', component: Students },
      { path: 'recordscores', name: 'teacher-recordscores', component: RecordScore },
      { path: 'assignment', name: 'teacher-assignment', component: Assignment },
      { path: 'markbook', name: 'teacher-markbook', component: MarkBook },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) return savedPosition;

    if (to.hash) {
      return new Promise((resolve) => {
        const MAX_ATTEMPTS = 40;
        let attempts = 0;

        const tryScroll = () => {
          const el = document.getElementById(to.hash.slice(1));

          if (el) {
            resolve({
              el,
              behavior: 'smooth',
              top: 80,
            });
            return;
          }

          attempts++;

          if (attempts >= MAX_ATTEMPTS) {
            resolve({ top: 0 });
            return;
          }

          window.requestAnimationFrame(() => {
            window.setTimeout(tryScroll, 50);
          });
        };

        tryScroll();
      });
    }

    return { top: 0 };
  }, 
});

// ... existing router creation ...

// Most of the routes above are a lazy `() => import(...)`. A deploy renames
// all of those chunks, so a tab left open across one navigates into a 404 (or
// the index.html the SPA fallback serves in its place) and the app quietly
// stops responding. Reload the document at the requested route instead — see
// chunkReloadRecovery.ts for the Sentry issues this closes.
installChunkReloadRecovery(router);

router.afterEach((to, from) => {
  // Tear down any tour DOM left behind when the route changes. The shell-mounted
  // useAppTour also handles this, but this is a belt-and-braces safety net for
  // cross-shell navigation (e.g. /admin -> /student) where the previous shell
  // unmounts and the new one is still booting.
  if (to.fullPath !== from.fullPath) {
    forceCleanupTourDom();
  }
});

router.beforeEach(async(to, from, next) => {
  const { useUserStore } = await import('../store/userStore');
  const modalStore = useOpenModalAsRoute();
  const userStore = useUserStore();
  const token = localStorage.getItem(LocalStorageKeys.token);

  // Neutral entry surfaces must render before tenant hydration so failures can
  // be retried without exposing a previous school's dashboard.
  if (to.name === 'switch-school' || to.name === 'select-school') {
    return next(token ? undefined : { name: 'login' });
  }

  // On tenant hosts (custom domain or school subdomain), the marketing landing pages
  // do not exist for users — send unauthenticated visitors straight to /login.
  // Authenticated users fall through to the /admin or /student redirect below.
  if (typeof to.name === 'string' && to.name.startsWith('landing') && isTenantHost() && !token) {
    return next({ name: 'login' });
  }

  // Already-authenticated users should never land on the login page — send them
  // back into the app. Hydrate the profile first if a token exists but the user
  // isn't loaded yet (e.g. a direct visit to /login), so we can route by role.
  // If the token is invalid/expired, getCurrentUser() returns null and we let
  // them stay on /login to re-authenticate.
  if (to.name === 'login' && token) {
    const user = userStore.currentUser ?? await userStore.getCurrentUser();
    if (user) {
      const typename = (user as { __typename?: string }).__typename;
      if (typename === 'Student') return next({ name: 'student-home' });
      return next({ name: userHomeRoute(userStore.isGuardianOnly) });
    }
  }

  if (from === START_LOCATION && token && !userStore.currentUser && to.name !== 'login') {
    const user = await userStore.getCurrentUser();
    if (user === null) {
      // The stored token no longer works: say so, and come back here after signing in.
      return next(loginLocation('expired', to.fullPath));
    }
  }

  if (to.path === '/' && userStore.currentUser) {
    const user = userStore.currentUser;
    if (user.__typename === 'User') {
      return next({ name: userHomeRoute(userStore.isGuardianOnly) });
    } else if (user.__typename === 'Student') {
      return next({ name: studentLandingRoute(userStore.isCbtOnlyStudentPortal) });
    }
  }

  // Keep staff, guardians and students inside their own portals.
  if (userStore.currentUser) {
    const redirect = portalRedirect(to, {
      userType: (userStore.currentUser as { __typename?: string }).__typename,
      isGuardianOnly: userStore.isGuardianOnly,
      hasGuardianAccess: userStore.isGuardian,
      studentLanding: studentLandingRoute(userStore.isCbtOnlyStudentPortal),
    });
    if (redirect) return next(redirect);
  }

  const authedUser = userStore.currentUser as { __typename?: string; is_active?: boolean } | null;

  // Block disabled students from accessing any student page except the disabled page
  if (
    authedUser?.__typename === 'Student' &&
    authedUser.is_active === false &&
    to.name !== 'student-account-disabled'
  ) {
    return next({ name: 'student-account-disabled' });
  }

  // If student is active but somehow on the disabled page, redirect to home
  if (
    authedUser?.__typename === 'Student' &&
    authedUser.is_active !== false &&
    to.name === 'student-account-disabled'
  ) {
    return next({ name: studentLandingRoute(userStore.isCbtOnlyStudentPortal) });
  }

  // CBT-only schools (#326): students may only visit the CBT surface —
  // any other student route (typed URL included) bounces to the exam page.
  if (
    authedUser?.__typename === 'Student' &&
    userStore.isCbtOnlyStudentPortal &&
    to.path.startsWith('/student') &&
    !isStudentRouteAllowed(to.name, true)
  ) {
    return next({ name: 'student-cbt' });
  }

  // ✅ Handle modal routes
  const routeConfig = to.name ? routeNames[to.name as keyof typeof routeNames] : undefined;
  if (userStore.isLoggedIn && routeConfig && 'usedInModal' in routeConfig && routeConfig.usedInModal) {
    modalStore.showModal();
  }

  if (to.meta.requiresAuth && !userStore.isLoggedIn) {
    return next(loginLocation(undefined, to.fullPath));
  }

  // Permission Check
  if (to.meta.permission) {
    const permission = to.meta.permission as { action: string, resource: string };
    if (!userStore.currentUser) {
      await userStore.getCurrentUser();
    }
    if (!userStore.currentUser) {
      return next('/login');
    }

    if (!can(userStore.currentUser, permission.action, permission.resource)) {
      const fallback = userHomeRoute(userStore.isGuardianOnly);
      if (from.name === fallback) {
        return next(false);
      }
      return next({ name: fallback });
    }
  }

  // Parent school guard
  if (to.meta.requiresParent) {
    const school = userStore.currentSchool as { is_parent?: boolean; parent_features_enabled?: boolean } | null;
    if (!school?.is_parent && !school?.parent_features_enabled) {
      return next({ name: userHomeRoute(userStore.isGuardianOnly) });
    }
  }

  next();
});

export default router;
